import { useEffect, lazy, Suspense, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";

import { PerformanceMonitor } from "@/components/ui/performance-monitor";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ErrorBoundary } from "@/components/layout/ErrorBoundary";
import { PageTransition } from "@/components/layout/PageTransition";
import { setupAuthListener } from "@/lib/auth-utils";
import { DemoProvider } from "@/contexts/DemoContext";
import { CookieConsentBanner } from "@/components/compliance/CookieConsentBanner";
import { RouteLoading } from "@/components/ui/route-loading";
import { LegalReacceptanceModal } from "@/components/compliance/LegalReacceptanceModal";
import { PolicyChangeNotification } from "@/components/compliance/PolicyChangeNotification";
import { useLegalVersionCheck } from "@/hooks/useLegalVersionCheck";
import { supabase } from "@/integrations/supabase/client";

// Critical routes - loaded immediately
import Index from "./pages/Index";
import Auth from "./pages/Auth";

// Lazy-loaded routes - code split for better initial load
const BrandsLanding = lazy(() => import("./pages/BrandsLanding"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Advertiser routes
const AdvertiserAuth = lazy(() => import("./pages/advertiser/AdvertiserAuth"));
const AdvertiserDashboard = lazy(() => import("./pages/advertiser/AdvertiserDashboard"));
const AdvertiserOnlyRoute = lazy(() => import("./components/advertiser/AdvertiserOnlyRoute").then(m => ({ default: m.AdvertiserOnlyRoute })));
const Campaigns = lazy(() => import("./pages/advertiser/Campaigns"));
const CampaignCreate = lazy(() => import("./pages/advertiser/CampaignCreate"));
const Balance = lazy(() => import("./pages/advertiser/Balance"));
const CampaignAnalytics = lazy(() => import("./pages/advertiser/CampaignAnalytics"));
const CampaignEdit = lazy(() => import("./pages/advertiser/CampaignEdit"));
const Analytics = lazy(() => import("./pages/advertiser/Analytics"));
const Account = lazy(() => import("./pages/advertiser/Account"));
const AudienceBuilder = lazy(() => import("./pages/advertiser/AudienceBuilder"));
const AdvertiserApplication = lazy(() => import("./pages/AdvertiserApplication"));

// User routes
const RoleSwitcher = lazy(() => import("./pages/RoleSwitcher"));
const Settings = lazy(() => import("./pages/Settings"));
const Profile = lazy(() => import("./pages/Profile"));
const UserPayouts = lazy(() => import("./pages/UserPayouts"));
const Leaderboard = lazy(() => import("./pages/Leaderboard"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const Brand = lazy(() => import("./pages/Brand"));
const Pings = lazy(() => import("./pages/Pings"));
const Referrals = lazy(() => import("./pages/Referrals"));

// Admin routes
const AdminAuth = lazy(() => import("./pages/admin/AdminAuth"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminApplications = lazy(() => import("./pages/admin/AdminApplications"));
const AdminUsers = lazy(() => import("./pages/admin/Users"));
const AdminPayouts = lazy(() => import("./pages/admin/Payouts"));
const AdminCampaigns = lazy(() => import("./pages/admin/Campaigns"));
const AdminAdvertisers = lazy(() => import("./pages/admin/Advertisers"));
const FraudDetection = lazy(() => import("./pages/admin/FraudDetection"));
const SecuritySettings = lazy(() => import("./pages/admin/SecuritySettings"));
const ActivityMonitor = lazy(() => import("./pages/admin/ActivityMonitor"));
const CampaignModeration = lazy(() => import("./pages/admin/CampaignModeration"));
const PlatformAnalytics = lazy(() => import("./pages/admin/PlatformAnalytics"));
const AdminSettings = lazy(() => import("./pages/admin/Settings"));
const AdminOnlyRoute = lazy(() => import("./components/admin/AdminOnlyRoute").then(m => ({ default: m.AdminOnlyRoute })));

// Legal routes
const LegalHub = lazy(() => import("./pages/legal/LegalHub"));
const TermsOfService = lazy(() => import("./pages/legal/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/legal/PrivacyPolicy"));
const DataDeletion = lazy(() => import("./pages/legal/DataDeletion"));
const CookiePolicy = lazy(() => import("./pages/legal/CookiePolicy"));
const ReferralTerms = lazy(() => import("./pages/legal/ReferralTerms"));
const TaxInformation = lazy(() => import("./pages/legal/TaxInformation"));
const AppPermissions = lazy(() => import("./pages/legal/AppPermissions"));
const ChildSafetyPolicy = lazy(() => import("./pages/legal/ChildSafetyPolicy"));
const AccessibilityStatement = lazy(() => import("./pages/legal/AccessibilityStatement"));
const TransparencyReport = lazy(() => import("./pages/legal/TransparencyReport"));
const DoNotSell = lazy(() => import("./pages/legal/DoNotSell"));

// Admin legal management
const LegalManagement = lazy(() => import("./pages/admin/LegalManagement"));

// Documentation routes
const DocsIndex = lazy(() => import("./pages/docs/Index"));
const GettingStarted = lazy(() => import("./pages/docs/GettingStarted"));
const GettingStartedUsers = lazy(() => import("./pages/docs/GettingStartedUsers"));
const GettingStartedAdvertisers = lazy(() => import("./pages/docs/GettingStartedAdvertisers"));
const GettingStartedAdmin = lazy(() => import("./pages/docs/GettingStartedAdmin"));
const UserJourneyOnboarding = lazy(() => import("./pages/docs/UserJourneyOnboarding"));
const UserJourneyDaily = lazy(() => import("./pages/docs/UserJourneyDaily"));
const UserJourneyEarnings = lazy(() => import("./pages/docs/UserJourneyEarnings"));
const UserJourneyReferrals = lazy(() => import("./pages/docs/UserJourneyReferrals"));
const AdvertiserCampaigns = lazy(() => import("./pages/docs/AdvertiserCampaigns"));
const AdvertiserTargeting = lazy(() => import("./pages/docs/AdvertiserTargeting"));
const AdvertiserAnalytics = lazy(() => import("./pages/docs/AdvertiserAnalytics"));
const AdvertiserBalance = lazy(() => import("./pages/docs/AdvertiserBalance"));
const AdminOverview = lazy(() => import("./pages/docs/AdminOverview"));
const DocsAdminUsers = lazy(() => import("./pages/docs/AdminUsers"));
const DocsAdminAdvertisers = lazy(() => import("./pages/docs/AdminAdvertisers"));
const DocsAdminPayouts = lazy(() => import("./pages/docs/AdminPayouts"));
const DocsAdminCampaigns = lazy(() => import("./pages/docs/AdminCampaigns"));
const AdminSecurity = lazy(() => import("./pages/docs/AdminSecurity"));
const DocsAdminSettings = lazy(() => import("./pages/docs/AdminSettings"));
const TechFrontend = lazy(() => import("./pages/docs/TechFrontend"));
const TechBackend = lazy(() => import("./pages/docs/TechBackend"));
const TechDatabase = lazy(() => import("./pages/docs/TechDatabase"));
const TechEdgeFunctions = lazy(() => import("./pages/docs/TechEdgeFunctions"));
const TechSecurity = lazy(() => import("./pages/docs/TechSecurity"));
const RewardMechanics = lazy(() => import("./pages/docs/RewardMechanics"));
const MobileCapacitor = lazy(() => import("./pages/docs/MobileCapacitor"));
const MobileNotifications = lazy(() => import("./pages/docs/MobileNotifications"));
const MobileFeatures = lazy(() => import("./pages/docs/MobileFeatures"));
const MobileResponsive = lazy(() => import("./pages/docs/MobileResponsive"));
const RewardStreaks = lazy(() => import("./pages/docs/RewardStreaks"));
const RewardBalance = lazy(() => import("./pages/docs/RewardBalance"));
const RewardPayouts = lazy(() => import("./pages/docs/RewardPayouts"));
const FeaturesInterests = lazy(() => import("./pages/docs/FeaturesInterests"));
const FeaturesNotifications = lazy(() => import("./pages/docs/FeaturesNotifications"));
const FeaturesPreferences = lazy(() => import("./pages/docs/FeaturesPreferences"));
const FeaturesLeaderboards = lazy(() => import("./pages/docs/FeaturesLeaderboards"));
const FeaturesLegal = lazy(() => import("./pages/docs/FeaturesLegal"));
const DocsNotFound = lazy(() => import("./pages/docs/DocsNotFound"));
const TroubleshootingPings = lazy(() => import("./pages/docs/TroubleshootingPings"));
const TroubleshootingPayouts = lazy(() => import("./pages/docs/TroubleshootingPayouts"));



const AppContent = () => {
  const [userId, setUserId] = useState<string | null>(null);
  const { data: documentsRequiringAcceptance, refetch: refetchLegalCheck } = useLegalVersionCheck(userId);

  useEffect(() => {
    const authListener = setupAuthListener();
    
    // Check for current user on mount
    const checkUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUserId(user?.id || null);
    };
    checkUser();
    
    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUserId(session?.user?.id || null);
      if (event === 'SIGNED_IN') {
        refetchLegalCheck();
      }
    });
    
    return () => {
      authListener.subscription.unsubscribe();
      subscription.unsubscribe();
    };
  }, [refetchLegalCheck]);

  return (
    <TooltipProvider>
      <DemoProvider>
      <Toaster />
      <Sonner />
      <PerformanceMonitor />
      <BrowserRouter>
        {userId && <PolicyChangeNotification userId={userId} />}
        {userId && documentsRequiringAcceptance && documentsRequiringAcceptance.length > 0 && (
          <LegalReacceptanceModal
            documents={documentsRequiringAcceptance}
            userId={userId}
            onAccepted={refetchLegalCheck}
          />
        )}
        <CookieConsentBanner />
              <Suspense fallback={<RouteLoading />}>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/brands" element={<PageTransition><BrandsLanding /></PageTransition>} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/onboarding" element={<PageTransition><Onboarding /></PageTransition>} />
                  <Route path="/dashboard" element={<PageTransition><Dashboard /></PageTransition>} />
                  <Route path="/advertiser/login" element={<PageTransition><AdvertiserAuth /></PageTransition>} />
                  <Route path="/advertiser" element={<PageTransition><AdvertiserOnlyRoute><AdvertiserDashboard /></AdvertiserOnlyRoute></PageTransition>} />
                  <Route path="/advertiser/campaigns" element={<PageTransition><AdvertiserOnlyRoute><Campaigns /></AdvertiserOnlyRoute></PageTransition>} />
                  <Route path="/advertiser/campaigns/new" element={<PageTransition><AdvertiserOnlyRoute><CampaignCreate /></AdvertiserOnlyRoute></PageTransition>} />
                  <Route path="/advertiser/campaigns/:id/edit" element={<PageTransition><AdvertiserOnlyRoute><CampaignEdit /></AdvertiserOnlyRoute></PageTransition>} />
                  <Route path="/advertiser/campaigns/:id/analytics" element={<PageTransition><AdvertiserOnlyRoute><CampaignAnalytics /></AdvertiserOnlyRoute></PageTransition>} />
                  <Route path="/advertiser/analytics" element={<PageTransition><AdvertiserOnlyRoute><Analytics /></AdvertiserOnlyRoute></PageTransition>} />
                  <Route path="/advertiser/account" element={<PageTransition><AdvertiserOnlyRoute><Account /></AdvertiserOnlyRoute></PageTransition>} />
                  <Route path="/advertiser/balance" element={<PageTransition><AdvertiserOnlyRoute><Balance /></AdvertiserOnlyRoute></PageTransition>} />
                  <Route path="/advertiser/audience-builder" element={<PageTransition><AdvertiserOnlyRoute><AudienceBuilder /></AdvertiserOnlyRoute></PageTransition>} />
                  <Route path="/advertiser/apply" element={<PageTransition><AdvertiserApplication /></PageTransition>} />
                  <Route path="/roles" element={<PageTransition><RoleSwitcher /></PageTransition>} />
                  <Route path="/admin/auth" element={<PageTransition><AdminAuth /></PageTransition>} />
                  <Route path="/admin" element={<PageTransition><AdminOnlyRoute><AdminDashboard /></AdminOnlyRoute></PageTransition>} />
                  <Route path="/admin/dashboard" element={<PageTransition><AdminOnlyRoute><AdminDashboard /></AdminOnlyRoute></PageTransition>} />
                  <Route path="/admin/applications" element={<PageTransition><AdminOnlyRoute><AdminApplications /></AdminOnlyRoute></PageTransition>} />
                  <Route path="/admin/users" element={<PageTransition><AdminOnlyRoute><AdminUsers /></AdminOnlyRoute></PageTransition>} />
                  <Route path="/admin/payouts" element={<PageTransition><AdminOnlyRoute><AdminPayouts /></AdminOnlyRoute></PageTransition>} />
                  <Route path="/admin/campaigns" element={<PageTransition><AdminOnlyRoute><AdminCampaigns /></AdminOnlyRoute></PageTransition>} />
                  <Route path="/admin/advertisers" element={<PageTransition><AdminOnlyRoute><AdminAdvertisers /></AdminOnlyRoute></PageTransition>} />
                  <Route path="/admin/fraud" element={<PageTransition><AdminOnlyRoute><FraudDetection /></AdminOnlyRoute></PageTransition>} />
                  <Route path="/admin/security" element={<PageTransition><AdminOnlyRoute><SecuritySettings /></AdminOnlyRoute></PageTransition>} />
                  <Route path="/admin/activity" element={<PageTransition><AdminOnlyRoute><ActivityMonitor /></AdminOnlyRoute></PageTransition>} />
                  <Route path="/admin/moderation" element={<PageTransition><AdminOnlyRoute><CampaignModeration /></AdminOnlyRoute></PageTransition>} />
                  <Route path="/admin/analytics" element={<PageTransition><AdminOnlyRoute><PlatformAnalytics /></AdminOnlyRoute></PageTransition>} />
                  <Route path="/admin/settings" element={<PageTransition><AdminOnlyRoute><AdminSettings /></AdminOnlyRoute></PageTransition>} />
                  <Route path="/admin/legal" element={<PageTransition><AdminOnlyRoute><LegalManagement /></AdminOnlyRoute></PageTransition>} />
                  <Route path="/settings" element={<PageTransition><Settings /></PageTransition>} />
                  <Route path="/profile" element={<PageTransition><Profile /></PageTransition>} />
                  <Route path="/payouts" element={<PageTransition><UserPayouts /></PageTransition>} />
                  <Route path="/pings" element={<PageTransition><Pings /></PageTransition>} />
                  <Route path="/referrals" element={<PageTransition><Referrals /></PageTransition>} />
                  <Route path="/how-it-works" element={<PageTransition><HowItWorks /></PageTransition>} />
                  <Route path="/brand-kit" element={<PageTransition><Brand /></PageTransition>} />
                  <Route path="/leaderboard" element={<PageTransition><Leaderboard /></PageTransition>} />
                  <Route path="/legal" element={<PageTransition><LegalHub /></PageTransition>} />
                  <Route path="/legal/terms" element={<PageTransition><TermsOfService /></PageTransition>} />
                  <Route path="/legal/privacy" element={<PageTransition><PrivacyPolicy /></PageTransition>} />
                  <Route path="/legal/data-deletion" element={<PageTransition><DataDeletion /></PageTransition>} />
                  <Route path="/legal/cookies" element={<PageTransition><CookiePolicy /></PageTransition>} />
                  <Route path="/legal/referral-terms" element={<PageTransition><ReferralTerms /></PageTransition>} />
                  <Route path="/legal/tax-information" element={<PageTransition><TaxInformation /></PageTransition>} />
                  <Route path="/legal/app-permissions" element={<PageTransition><AppPermissions /></PageTransition>} />
                  <Route path="/legal/child-safety" element={<PageTransition><ChildSafetyPolicy /></PageTransition>} />
                  <Route path="/legal/accessibility" element={<PageTransition><AccessibilityStatement /></PageTransition>} />
                  <Route path="/legal/transparency" element={<PageTransition><TransparencyReport /></PageTransition>} />
                  <Route path="/legal/do-not-sell" element={<PageTransition><DoNotSell /></PageTransition>} />
                  <Route path="/docs" element={<PageTransition><DocsIndex /></PageTransition>} />
                  <Route path="/docs/getting-started" element={<PageTransition><GettingStarted /></PageTransition>} />
                  <Route path="/docs/getting-started/users" element={<PageTransition><GettingStartedUsers /></PageTransition>} />
                  <Route path="/docs/getting-started/advertisers" element={<PageTransition><GettingStartedAdvertisers /></PageTransition>} />
                  <Route path="/docs/getting-started/admin" element={<PageTransition><GettingStartedAdmin /></PageTransition>} />
                  <Route path="/docs/user-journey/onboarding" element={<PageTransition><UserJourneyOnboarding /></PageTransition>} />
                  <Route path="/docs/user-journey/daily" element={<PageTransition><UserJourneyDaily /></PageTransition>} />
                  <Route path="/docs/user-journey/earnings" element={<PageTransition><UserJourneyEarnings /></PageTransition>} />
                  <Route path="/docs/user-journey/referrals" element={<PageTransition><UserJourneyReferrals /></PageTransition>} />
                  <Route path="/docs/advertiser/campaigns" element={<PageTransition><AdvertiserCampaigns /></PageTransition>} />
                  <Route path="/docs/advertiser/targeting" element={<PageTransition><AdvertiserTargeting /></PageTransition>} />
                  <Route path="/docs/advertiser/analytics" element={<PageTransition><AdvertiserAnalytics /></PageTransition>} />
                  <Route path="/docs/advertiser/balance" element={<PageTransition><AdvertiserBalance /></PageTransition>} />
                  <Route path="/docs/admin/overview" element={<PageTransition><AdminOverview /></PageTransition>} />
                  <Route path="/docs/admin/users" element={<PageTransition><DocsAdminUsers /></PageTransition>} />
                  <Route path="/docs/admin/advertisers" element={<PageTransition><DocsAdminAdvertisers /></PageTransition>} />
                  <Route path="/docs/admin/payouts" element={<PageTransition><DocsAdminPayouts /></PageTransition>} />
                  <Route path="/docs/admin/campaigns" element={<PageTransition><DocsAdminCampaigns /></PageTransition>} />
                  <Route path="/docs/admin/security" element={<PageTransition><AdminSecurity /></PageTransition>} />
                  <Route path="/docs/admin/settings" element={<PageTransition><DocsAdminSettings /></PageTransition>} />
                  <Route path="/docs/tech/frontend" element={<PageTransition><TechFrontend /></PageTransition>} />
                  <Route path="/docs/tech/backend" element={<PageTransition><TechBackend /></PageTransition>} />
                  <Route path="/docs/tech/database" element={<PageTransition><TechDatabase /></PageTransition>} />
                  <Route path="/docs/tech/edge-functions" element={<PageTransition><TechEdgeFunctions /></PageTransition>} />
                  <Route path="/docs/tech/security" element={<PageTransition><TechSecurity /></PageTransition>} />
                  <Route path="/docs/mobile/capacitor" element={<PageTransition><MobileCapacitor /></PageTransition>} />
                  <Route path="/docs/mobile/notifications" element={<PageTransition><MobileNotifications /></PageTransition>} />
                  <Route path="/docs/mobile/features" element={<PageTransition><MobileFeatures /></PageTransition>} />
                  <Route path="/docs/mobile/responsive" element={<PageTransition><MobileResponsive /></PageTransition>} />
                  <Route path="/docs/rewards/mechanics" element={<PageTransition><RewardMechanics /></PageTransition>} />
                  <Route path="/docs/rewards/streaks" element={<PageTransition><RewardStreaks /></PageTransition>} />
                  <Route path="/docs/rewards/balance" element={<PageTransition><RewardBalance /></PageTransition>} />
                  <Route path="/docs/rewards/payouts" element={<PageTransition><RewardPayouts /></PageTransition>} />
                  <Route path="/docs/features/interests" element={<PageTransition><FeaturesInterests /></PageTransition>} />
                  <Route path="/docs/features/notifications" element={<PageTransition><FeaturesNotifications /></PageTransition>} />
                  <Route path="/docs/features/preferences" element={<PageTransition><FeaturesPreferences /></PageTransition>} />
                  <Route path="/docs/features/leaderboards" element={<PageTransition><FeaturesLeaderboards /></PageTransition>} />
                  <Route path="/docs/features/legal" element={<PageTransition><FeaturesLegal /></PageTransition>} />
                  <Route path="/docs/troubleshooting/pings" element={<PageTransition><TroubleshootingPings /></PageTransition>} />
                  <Route path="/docs/troubleshooting/payouts" element={<PageTransition><TroubleshootingPayouts /></PageTransition>} />
                  <Route path="/docs/*" element={<PageTransition><DocsNotFound /></PageTransition>} />
                  <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
                </Routes>
              </Suspense>
            </BrowserRouter>
      </DemoProvider>
    </TooltipProvider>
  );
};

const App = () => {
  return (
    <ErrorBoundary>
      <AppContent />
    </ErrorBoundary>
  );
};

export default App;
